import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import FilterSelect from '../Common/FilterSelect';
import salesforceFundraisingsService from 'services/salesforceFundraisingsService';

const FILTER_MAX_LIST_HEIGHT = 200;

const BOOLEAN_OPTIONS = [
	{ label: 'Yes', value: 'true' },
	{ label: 'No', value: 'false' },
];

const INITIAL_FILTERS = {
	investor_type: '',
	active: '',
	existing_investor: '',
	stage: '',
	closing_round: '',
	commitment_date_from: '',
	commitment_date_to: '',
};

const ALL_INVESTOR_TYPES = { label: 'All types', value: '' };
const ALL_ACTIVE = { label: 'Active: All', value: '' };
const ALL_EXISTING_INVESTOR = { label: 'Existing Investor: All', value: '' };
const ALL_STAGES = { label: 'All stages', value: '' };
const ALL_CLOSING_ROUNDS = { label: 'All closing rounds', value: '' };

const activeOptions = [ALL_ACTIVE, ...BOOLEAN_OPTIONS];
const existingInvestorOptions = [ALL_EXISTING_INVESTOR, ...BOOLEAN_OPTIONS];

const FundraisingFilters = ({ filters, onChange }) => {
	const [picklistValues, setPicklistValues] = useState(null);
	const picklistFetched = useRef(false);

	useEffect(() => {
		if (picklistFetched.current) return;

		const fetchPicklists = async () => {
			try {
				const data = await salesforceFundraisingsService.getPicklistValues();
				setPicklistValues(data);
				picklistFetched.current = true;
			} catch (err) {
				console.error('Failed to load picklist values:', err);
			}
		};

		fetchPicklists();
	}, []);

	const investorTypeOptions = [
		ALL_INVESTOR_TYPES,
		...(picklistValues?.investor_type || []),
	];
	const stageOptions = [ALL_STAGES, ...(picklistValues?.stage || [])];
	const closingRoundOptions = [
		ALL_CLOSING_ROUNDS,
		...(picklistValues?.closing_round || []),
	];

	const handleChange = (key, value) => {
		onChange({ ...filters, [key]: value });
	};

	const handleDateChange = (key, date) => {
		if (date === null) {
			handleChange(key, '');
			return;
		}
		if (!date.isValid()) return;

		// Validate date range
		if (key === 'commitment_date_from' && filters.commitment_date_to) {
			const toDate = dayjs(filters.commitment_date_to);
			if (date.isAfter(toDate)) return;
		}
		if (key === 'commitment_date_to' && filters.commitment_date_from) {
			const fromDate = dayjs(filters.commitment_date_from);
			if (date.isBefore(fromDate)) return;
		}

		const formatted = date.format('YYYY-MM-DD');
		handleChange(key, formatted);
	};

	const hasActiveFilters = Object.values(filters).some((v) => v !== '');

	const handleReset = () => {
		onChange({ ...INITIAL_FILTERS });
	};

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 1,
				padding: '8px 14px',
				borderBottom: '0.5px solid',
				borderColor: 'border.light',
				flexWrap: 'wrap',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 2,
					flex: 1,
					flexWrap: 'wrap',
				}}
			>
				<FilterSelect
					maxListHeight={FILTER_MAX_LIST_HEIGHT}
					value={filters.investor_type}
					options={investorTypeOptions}
					onChange={(val) => handleChange('investor_type', val)}
				/>

				<FilterSelect
					maxListHeight={FILTER_MAX_LIST_HEIGHT}
					value={filters.active}
					options={activeOptions}
					onChange={(val) => handleChange('active', val)}
				/>

				<FilterSelect
					maxListHeight={FILTER_MAX_LIST_HEIGHT}
					value={filters.existing_investor}
					options={existingInvestorOptions}
					onChange={(val) => handleChange('existing_investor', val)}
				/>

				<FilterSelect
					maxListHeight={FILTER_MAX_LIST_HEIGHT}
					value={filters.stage}
					options={stageOptions}
					onChange={(val) => handleChange('stage', val)}
				/>

				<FilterSelect
					maxListHeight={FILTER_MAX_LIST_HEIGHT}
					value={filters.closing_round}
					options={closingRoundOptions}
					onChange={(val) => handleChange('closing_round', val)}
				/>

				<DatePicker
					label='From'
					value={
						filters.commitment_date_from
							? dayjs(filters.commitment_date_from)
							: null
					}
					maxDate={
						filters.commitment_date_to
							? dayjs(filters.commitment_date_to)
							: null
					}
					onAccept={(date) => handleDateChange('commitment_date_from', date)}
					slotProps={{
						textField: {
							size: 'small',
							sx: {
								width: 140,
							},
							onKeyDown: (e) => {
								if (e.key === 'Enter') {
									handleDateChange(
										'commitment_date_from',
										dayjs(e.target.value)
									);
								}
							},
							onBlur: (e) => {
								handleDateChange('commitment_date_from', dayjs(e.target.value));
							},
						},
					}}
				/>

				<DatePicker
					label='To'
					value={
						filters.commitment_date_to
							? dayjs(filters.commitment_date_to)
							: null
					}
					minDate={
						filters.commitment_date_from
							? dayjs(filters.commitment_date_from)
							: null
					}
					onAccept={(date) => handleDateChange('commitment_date_to', date)}
					slotProps={{
						textField: {
							size: 'small',
							sx: {
								width: 140,
							},
							onKeyDown: (e) => {
								if (e.key === 'Enter') {
									handleDateChange('commitment_date_to', dayjs(e.target.value));
								}
							},
							onBlur: (e) => {
								handleDateChange('commitment_date_to', dayjs(e.target.value));
							},
						},
					}}
				/>
			</Box>

			{hasActiveFilters && (
				<Button
					variant='outlined'
					size='small'
					onClick={handleReset}
					sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}
				>
					Reset filters
				</Button>
			)}
		</Box>
	);
};

FundraisingFilters.propTypes = {
	filters: PropTypes.shape({
		investor_type: PropTypes.string.isRequired,
		active: PropTypes.string.isRequired,
		existing_investor: PropTypes.string.isRequired,
		stage: PropTypes.string.isRequired,
		closing_round: PropTypes.string.isRequired,
		commitment_date_from: PropTypes.string.isRequired,
		commitment_date_to: PropTypes.string.isRequired,
	}).isRequired,
	onChange: PropTypes.func.isRequired,
};

export { INITIAL_FILTERS };
export default FundraisingFilters;
